import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { formatMoney } from "./utils";
import type { CashierLabels } from "./types";

export interface CashierCartTotalsProps {
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  currencyPrefix: string;
  labels: CashierLabels;
  locale?: "tr" | "en";
}

export function CashierCartTotals({
  subtotalMinor, discountMinor, taxMinor, totalMinor, currencyPrefix, labels, locale = "tr",
}: CashierCartTotalsProps) {
  return (
    <div className="px-4 py-3 border-t border-biz-border bg-biz-surface space-y-1.5">
      <Row label={labels.cart.subtotal}>
        <Tween value={subtotalMinor}>
          {(v) => (
            <span className="font-mono-num text-sm text-biz-foreground tabular-nums">
              {formatMoney(v, currencyPrefix, locale)}
            </span>
          )}
        </Tween>
      </Row>
      {discountMinor > 0 && (
        <Row label={labels.cart.discount}>
          <Tween value={-discountMinor}>
            {(v) => (
              <span className="font-mono-num text-sm text-biz-success tabular-nums">
                {formatMoney(v, currencyPrefix, locale)}
              </span>
            )}
          </Tween>
        </Row>
      )}
      <Row label={labels.cart.tax}>
        <Tween value={taxMinor}>
          {(v) => (
            <span className="font-mono-num text-sm text-biz-foreground-muted tabular-nums">
              {formatMoney(v, currencyPrefix, locale)}
            </span>
          )}
        </Tween>
      </Row>
      <div className="h-px bg-biz-border my-2" />
      <Row label={labels.cart.total} large>
        <Tween value={totalMinor}>
          {(v) => (
            <motion.span
              key={v}
              initial={{ scale: 0.96, opacity: 0.7 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.18 }}
              className={cn(
                "font-mono-num font-bold text-biz-foreground tabular-nums",
                "text-2xl md:text-3xl",
              )}
            >
              {formatMoney(v, currencyPrefix, locale)}
            </motion.span>
          )}
        </Tween>
      </Row>
    </div>
  );
}

function Row({ label, children, large }: { label: string; children: React.ReactNode; large?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={cn("text-biz-foreground-muted", large ? "text-sm font-bold uppercase tracking-wider" : "text-sm")}>
        {label}
      </span>
      {children}
    </div>
  );
}

/** Lightweight smooth value transition without external dep. */
function Tween({
  value, children,
}: { value: number; children: (v: number) => React.ReactNode }) {
  const [v, setV] = React.useState(value);
  const fromRef = React.useRef(value);
  const startRef = React.useRef<number>(0);
  const rafRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setV(value);
      fromRef.current = value;
      return;
    }
    const from = fromRef.current;
    const to = value;
    const dur = 180;
    startRef.current = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - startRef.current) / dur);
      const ease = 1 - Math.pow(1 - p, 3);
      setV(Math.round(from + (to - from) * ease));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value]);

  return <>{children(v)}</>;
}
